import { Component, isDevMode, OnDestroy } from '@angular/core';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';

import { OidcSecurityService } from 'angular-auth-oidc-client';
import { forkJoin, fromEvent, merge, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { TabSyncService } from '@auth/service/tab-sync.service';
import { NotificationsContainerComponent } from '@common/component/notification/notification-container.component';
import { PreviewBannerComponent } from '@common/component/preview-banner/preview-banner.component';
import { AUTH_POPUP_COMPLETE_PATH, AuthChannelService } from '@common/service/auth-channel.service';
import { AuthSessionService } from '@common/service/auth-session.service';
import { BuildInfoService } from '@common/service/build-info.service';
import { ComponentBroadcastService } from '@common/service/component-broadcast.service';
import { DataTopicDiagnosticsService } from '@common/service/data-topic-diagnostics.service';
import { DomainService } from '@common/service/domain.service';
import { FeatureFlagService } from '@common/service/feature-flag.service';
import { GlobalNotificationService } from '@common/service/global-notification.service';
import { GlobalVarsService } from '@common/service/global-vars.service';
import { GtmService } from '@common/service/gtm.service';
import { LocalStorageService } from '@common/service/localstorage.service';
import { NotificationCenterService } from '@common/service/notification-center.service';
import { OutageService } from '@common/service/outage.service';
import { SessionExpiryService } from '@common/service/session-expiry.service';
import { SessionStorageService } from '@common/service/sessionstorage.service';
import { SocketBankService } from '@common/service/socket-bank.service';
import { SocketService } from '@common/service/socket.service';
import { SystemNotificationService } from '@common/service/system-notification.service';
import { UserService } from '@common/service/user.service';
import { DomainQuery } from '@common/store/domain.store';
import { UserQuery } from '@common/store/user.store';
import {
  CollectionCommandBroadcastPayload,
  ConversationUpdatedData,
  DatasourceBroadcastBusEvent,
  DatasourceBroadcastPayload,
  FormCommandBroadcastPayload,
  FormOperationData,
  GlobalBotMessageData,
  MessageType,
  SocketEventType,
  SocketMessage,
  SocketNotificationData,
  TableBroadcastPayload,
  UserVariableOperationData,
} from '@common/types';
import { SocketImpl } from '@common/types/socket-impl';
import {
  LIVE_RESYNC_DEDUPE_MS,
  LIVE_RESYNC_MIN_GAP_MS,
  SOCKET_BANK_ID_GLOBAL,
  SOCKET_OPTIONS,
  STORAGE_KEY_ENTITY_ID,
  STORAGE_KEY_USER_ID,
  URL_FRAGMENT_REDIRECT_URL,
} from '@common/util/constants';
import { deepCopy, getNestedValue, isEmpty } from '@common/util/util';

import type { LinkSentState } from '@auth/component/fresh-link-sent/fresh-link-sent.params';

/** AIWB-1088: how long to wait after closeWindow() before assuming it was refused. */
const POPUP_CLOSE_CHECK_MS = 500;

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NotificationsContainerComponent, PreviewBannerComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnDestroy {
  constructor(
    private localstorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private userService: UserService,
    private socketService: SocketService,
    private socketBankService: SocketBankService,
    private oidcSecurityService: OidcSecurityService,
    private gtmService: GtmService,
    private globalNotificationService: GlobalNotificationService,
    private notificationCenterService: NotificationCenterService,
    private buildInfoService: BuildInfoService,
    private broadcastService: ComponentBroadcastService,
    private tabSyncService: TabSyncService,
    private domainService: DomainService,
    private domainQuery: DomainQuery,
    private userQuery: UserQuery,
    private systemNotification: SystemNotificationService,
    private featureFlagService: FeatureFlagService,
    private authSessionService: AuthSessionService,
    private globalVarsService: GlobalVarsService,
    private authChannel: AuthChannelService,
    private sessionExpiry: SessionExpiryService,
    private outageService: OutageService,
    private dataTopicDiagnostics: DataTopicDiagnosticsService,
  ) {
    // Parse query parameters from the current URL
    const url = new URL(window.location.href);
    const queryParams: { [key: string]: string } = {};
    url.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    // AK-476 cross-domain handoff: the broker callback returns to
    // /auth?sessionId=...&redirectUrl=.... Mint the cookie on THIS origin, then do
    // a FULL-PAGE redirect so the destination boots fresh and reliably reads the
    // established session + user. Handled before (and instead of) the normal
    // bootstrap — we're navigating away.
    if (queryParams['sessionId']) {
      this.completeBrokerHandoff(queryParams['sessionId'], queryParams['redirectUrl']);
      return;
    }

    // AIWB-1088: a direct hit on the popup landing (broker came back without a sessionId, or a
    // stray navigation) — never boot the app here: close the popup, or go home if we can't.
    if (window.location.pathname === AUTH_POPUP_COMPLETE_PATH) {
      this.finishPopup(null);
      return;
    }

    // AK-476: an OIDC callback (?code=&state=) is unambiguously the JWT/OIDC flow
    // — cookie mode hands back a one-time `sessionId` (handled above), never a
    // browser-side `code`. The one-time authorization-code exchange (checkAuth →
    // POST /oauth2/token) MUST run before any /api/domain request, because the
    // domain request's interceptor calls oidcSecurityService.getAccessToken(),
    // which initialises the OIDC client against the callback URL and breaks the
    // code exchange (400 at /token). So for a callback we keep the legacy
    // auth-first order; otherwise we load the domain FIRST so the cookie-vs-OIDC
    // toggle can resolve from the domain `features` node (+ env override).
    const oidcCallback = !!(queryParams['code'] && queryParams['state']);
    const bootstrap$ = oidcCallback
      ? this.userService.authUser(queryParams).pipe(switchMap(() => this.ensureDomainLoaded()))
      : this.ensureDomainLoaded().pipe(switchMap(() => this.bootstrapAuth(queryParams)));

    bootstrap$
      .pipe(
        catchError((error) => {
          console.error('user auth error', error);
          this.isAppError = true;
          this.isUserUnauthenticated = true;
          this.appErrorStatusText = error.message;
          return of(null);
        }),
        tap(() => {
          if (!this.routeAuthOutcome(queryParams)) {
            this.restoreRedirect(queryParams);
          }
        }),
        switchMap(() => {
          return this.userService.getUserInfo().pipe(
            tap((user) => {
              if (isEmpty(user)) {
                this.isAppError = true;
                this.isUserUnauthenticated = true;
              }
            }),
            catchError((e) => {
              this.isAppError = true;
              this.appErrorStatusText = e.message;
              console.error('error in user info', e);
              return of(null);
            }),
          );
        }),
        take(1),
        catchError((error) => {
          console.error('error in user auth or info', error);
          this.isAppError = true;
          this.isUserUnauthenticated = true;
          this.appErrorStatusText = error.message;
          return of(null);
        }),
        tap(() => {
          this.initSocket();
        }),
        tap(() => {
          this.isAuthCompleted = true;
          // OIDC token renewal only — cookie sessions have no client token to renew.
          if (!this.featureFlagService.cookieSessionMigrationEnabled && this.tabSyncService.isLeader()) {
            console.debug('Authentication flow completed, enabling token renewal');
            this.setupTokenRenewalTimer();
          }
        }),
      )
      .subscribe();
  }

  /**
   * Choose and run the auth bootstrap (AK-476 WP-F5). Domain config is already
   * loaded, so the cookie-session toggle reflects the domain `features` node + env override.
   *
   * Cookie mode: read the API session. If unauthenticated, proceed as guest —
   * the loginGuard drives login (navigate to /api/auth/login/start) on protected
   * routes. We deliberately do NOT fall back to OIDC here: under broker mode the
   * OIDC dance can't complete and only adds latency/noise (the openid-config
   * discovery calls). Toggle OFF: today's OIDC bootstrap, unchanged.
   */
  /**
   * Resolve the domain config once: emit the cached domain if present, otherwise
   * fetch it. The cookie-session toggle reads the domain `features` node, so this
   * must complete before the cookie-vs-OIDC decision in bootstrapAuth().
   */
  private ensureDomainLoaded(): Observable<unknown> {
    return this.domainQuery.domain$.pipe(
      take(1),
      switchMap((domain) => (isEmpty(domain) ? this.domainService.fetchDomainInfo() : of(domain))),
      // AIWB-1175: the app has booted once the domain is loaded; from here an outage may offer Stop waiting.
      tap(() => this.outageService.markBooted()),
    );
  }

  private bootstrapAuth(queryParams: { [key: string]: string }): Observable<unknown> {
    if (this.featureFlagService.cookieSessionMigrationEnabled) {
      // Read the current session from the aiwb_session cookie. (The cross-domain
      // sessionId handoff is handled separately in the constructor.)
      return this.authSessionService.load().pipe(
        tap((session) =>
          console.debug(
            session.authenticated
              ? '[AK-476] Cookie session authenticated'
              : '[AK-476] No cookie session; proceeding as guest',
          ),
        ),
        // AIWB-1088: arm proactive expiry detection (inert unless the session is authenticated).
        tap(() => this.sessionExpiry.start()),
        map(() => null),
      );
    }
    return this.userService.authUser(queryParams);
  }

  /**
   * AK-476 cross-domain handoff: exchange the one-time `sessionId` for an
   * `aiwb_session` cookie on THIS origin (POST), then full-page-redirect to the
   * target so the destination boots fresh and reads the established session +
   * user. A full reload (per the AK-474 spec) avoids relying on a same-tick cookie
   * read and guarantees auth state/UI are correct on arrival.
   */
  private completeBrokerHandoff(sessionId: string, redirectUrl?: string): void {
    const isPopup = this.isPopupCompleteTarget(redirectUrl);
    this.authSessionService
      .establishSession(sessionId)
      .pipe(take(1))
      .subscribe((ok) => {
        if (isPopup) {
          // AIWB-1088: report to the opener tab(s) and close; the opener restores in place.
          this.finishPopup(ok);
          return;
        }
        this.externalRedirect(
          // AK-604: a failed exchange surfaces the visible outcome page instead of a
          // silent guest landing. Full-page (like the success arm) so the app boots
          // fresh on arrival. The success path is unchanged and load-bearing.
          ok ? this.safeRedirect(redirectUrl) : `${window.location.origin}/auth/link-sent?state=failed`,
        );
      });
  }

  /** True only for a SAME-ORIGIN /auth/popup-complete target. */
  private isPopupCompleteTarget(redirectUrl?: string): boolean {
    if (!redirectUrl) {
      return false;
    }
    try {
      const target = new URL(redirectUrl, window.location.origin);
      return target.origin === window.location.origin && target.pathname === AUTH_POPUP_COMPLETE_PATH;
    } catch {
      return false;
    }
  }

  /**
   * AIWB-1088 popup side: `ok` true/false → post session-restored / session-restore-failed on
   * `aiwb-auth` (null → post nothing), then close. If the browser refuses window.close()
   * (window not script-opened), fall back to home as today.
   */
  private finishPopup(ok: boolean | null): void {
    if (ok !== null) {
      this.authChannel.post({ type: ok ? 'session-restored' : 'session-restore-failed' });
    }
    this.closeWindow();
    setTimeout(() => {
      if (!this.isWindowClosed()) {
        this.externalRedirect(this.safeRedirect('/'));
      }
    }, POPUP_CLOSE_CHECK_MS);
  }

  /** window.close seam (stubbed in tests). */
  private closeWindow(): void {
    window.close();
  }

  /** window.closed seam (stubbed in tests). */
  private isWindowClosed(): boolean {
    return window.closed;
  }

  /** Same-origin redirect target (defends against open redirect); falls back to home. */
  private safeRedirect(redirectUrl?: string): string {
    const home = `${window.location.origin}/`;
    if (!redirectUrl) {
      return home;
    }
    try {
      const target = new URL(redirectUrl, window.location.origin);
      return target.origin === window.location.origin ? target.toString() : home;
    } catch {
      return home;
    }
  }

  /** Full-page navigation seam (overridable in tests). */
  private externalRedirect(dest: string): void {
    window.location.assign(dest);
  }

  /**
   * AK-604: the server surfaces magic-link outcomes as query params on the SITE
   * ROOT origin URL — `/?magic=resent` after regenerating an already-consumed
   * link, `/?error=rate_limited` when issuance was throttled. Map them to the
   * corresponding visible outcome on the existing landing page. NO token oracle:
   * a resent link maps to the neutral `sent` state, which never reveals whether
   * the prior link was expired, consumed, or never existed.
   */
  private authOutcomeState(queryParams: { [key: string]: string }): LinkSentState | undefined {
    if (queryParams['magic'] === 'resent') {
      return 'sent';
    }
    if (queryParams['error'] === 'rate_limited') {
      return 'rate_limited';
    }
    return undefined;
  }

  /**
   * Route a root-origin auth outcome to `/auth/link-sent?state=...` so the user
   * sees WHY they are not signed in instead of a silent guest landing. Runs
   * post-bootstrap (same hook as restoreRedirect). `replaceUrl` scrubs the
   * one-time outcome param from the address bar and history, so a reload or
   * back-navigation cannot re-trigger it. Returns whether it navigated.
   */
  private routeAuthOutcome(queryParams: { [key: string]: string }): boolean {
    const state = this.authOutcomeState(queryParams);
    if (!state) {
      return false;
    }
    this.router.navigate(['/auth/link-sent'], { queryParams: { state }, replaceUrl: true });
    return true;
  }

  /**
   * Post-bootstrap navigation.
   *
   * AK-476 cross-domain handoff: the broker callback returns to
   * `/auth?sessionId=...&redirectUrl=...`. After the handoff exchange we must
   * leave that one-time URL (replaceUrl, so it isn't in history / re-run on
   * back), going to the requested redirect on success or home otherwise.
   *
   * Otherwise this restores the legacy `redirect_url` query param if present (path, query and hash, so a
   * dialog link keeps its `dlg`/`entity`; never the origin).
   */
  private restoreRedirect(queryParams: { [key: string]: string }): void {
    // Legacy OIDC `redirect_url` query param (the cross-domain sessionId handoff is
    // handled in the constructor with a full-page redirect).
    const redirectUrl = queryParams[URL_FRAGMENT_REDIRECT_URL];
    if (!redirectUrl) {
      return;
    }
    const url = new URL(redirectUrl, window.location.origin);
    // Path + query + hash only: the origin is discarded, so an absolute external `redirect_url` can only
    // ever land in-app. The query is kept (a dialog link's `dlg`/`entity`), and `navigateByUrl` takes the
    // already-encoded string as is.
    // Never navigate on a non-path (`javascript:`, `data:`) and never hand over a protocol-relative `//x`.
    if (!url.pathname.startsWith('/')) {
      return;
    }
    this.router.navigateByUrl(url.pathname.replace(/^\/{2,}/, '/') + url.search + url.hash);
  }

  isAppError = false;
  isUserUnauthenticated = false;
  appErrorStatusText = '';
  /**
   * Socket message types this component bridges onto the in-app broadcast bus.
   *
   * This list gates the socket stream BEFORE the switch in initGlobalNotifications() is reached,
   * so every type handled by that switch MUST appear here — a missing entry silently drops the
   * whole envelope with no warning anywhere (AK-646: `collection_broadcast` was unreachable for
   * exactly that reason). Covered by app.component.spec.ts.
   */
  readonly allowedSocketMessageTypes = [
    MessageType.NOTIFICATION,
    MessageType.USER_VAR,
    MessageType.GLOBAL_BOT,
    MessageType.FORM_BROADCAST,
    MessageType.TABLE_BROADCAST,
    MessageType.COLLECTION_BROADCAST,
    MessageType.DATASOURCE_BROADCAST,
    MessageType.CONVERSATION_UPDATED,
  ];
  private destroy$ = new Subject<void>();
  private tokenRenewalTimer: NodeJS.Timeout | null = null;
  private isAuthCompleted = false;
  // AIWB-693: mint a guest session at most once per bootstrap. Lives here (not on
  // AuthSessionService — see J7 in the plan) because "once per bootstrap" is
  // socket-bootstrap policy; AppComponent is recreated on a real login reload, so
  // this resets naturally.
  private guestSessionAttempted = false;

  ngOnInit() {
    this.buildInfoService.logBuildInfo();
    this.gtmService.initGTM();

    // Listen for entity switches from other tabs
    this.setupEntitySwitchListener();

    // AK-476: the OIDC tab-sync layer (focus-based "logged-out in another tab"
    // detection + leader election) and token renewal are OIDC-specific and misfire
    // in cookie-session mode (the focus check compares OIDC-only state and pops the
    // dialog on every focus). Disable both entirely in cookie mode. The toggle is
    // domain-driven and NOT yet resolved at ngOnInit, so wait for the domain to
    // load before deciding — otherwise the (still-false) toggle would let the
    // OIDC tab-sync run before cookie mode is known.
    this.domainQuery.domain$
      .pipe(
        filter((domain) => !isEmpty(domain)),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (!this.featureFlagService.cookieSessionMigrationEnabled) {
          // Legacy OIDC only: start the BroadcastChannel + leader election now that
          // we know cookie mode is OFF. In cookie mode init() is never called, so the
          // tab-sync service stays inert (no leader race, no false logout dialog).
          this.tabSyncService.init();
          this.setupFocusListener();
          this.setupTokenRenewalMonitoring();
        }
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      const params = new URLSearchParams(window.location.search);

      const urlEntityId = params.get('entity_id');
      if (urlEntityId) {
        this.localstorageService.set(STORAGE_KEY_ENTITY_ID, urlEntityId);
      }
    });

    this.initGlobalNotifications();
  }

  private initSocket(accessToken?: string) {
    // AK-476 (WP-F5): in cookie-session mode the socket authenticates with a
    // one-shot ticket (no Bearer token). Fetch a fresh ticket immediately before
    // connecting, per the API contract.
    if (this.featureFlagService.cookieSessionMigrationEnabled) {
      this.authSessionService
        .getSocketTicket()
        .pipe(take(1))
        .subscribe((ticket) => {
          if (ticket) {
            this.createSocketWithTicket(ticket);
          } else {
            this.mintGuestSessionAndRetrySocket();
          }
        });
      return;
    }

    if (accessToken) {
      this.createSocketWithToken(accessToken);
    } else {
      this.oidcSecurityService
        .getAccessToken()
        .pipe(
          filter((token) => !!token),
          take(1),
          tap((token) => this.createSocketWithToken(token)),
        )
        .subscribe();
    }
  }

  /**
   * AIWB-693: a public page has no logged-in identity, so the ticket fetch
   * above came back empty. Mint a short-lived guest session — AT MOST ONCE per
   * bootstrap, guarded by `guestSessionAttempted` — then retry the ticket fetch
   * exactly once. Still no ticket after the retry (or the mint itself refused)
   * is a normal state for a public page, not an error: debug log, no socket.
   */
  private mintGuestSessionAndRetrySocket(): void {
    if (this.guestSessionAttempted) {
      console.debug('[Socket] no ticket; guest session already attempted this bootstrap, skipping.');
      return;
    }
    // AIWB-693 round-1 review: getSocketTicket() resolves to null on ANY
    // transport failure, not only "no session" — so a transient error during an
    // authenticated cookie-mode bootstrap can land here too. Never mint (or
    // retry via) a guest session for an already-authenticated user; that would
    // silently downgrade them if the guest endpoint replaces the session
    // cookie. The transient-failure case itself is a distinct, pre-existing
    // gap left as-is — this only stops it from misrouting through the guest
    // path.
    if (this.authSessionService.isAuthenticated) {
      console.debug('[Socket] no ticket for an authenticated session; skipping guest-session mint.');
      return;
    }
    this.guestSessionAttempted = true;

    this.authSessionService
      .ensureGuestSession()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((minted) => {
        if (!minted) {
          console.debug('[Socket] guest session mint failed; socket creation skipped.');
          return;
        }
        this.authSessionService
          .getSocketTicket()
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe((ticket) => {
            if (ticket) {
              this.createSocketWithTicket(ticket);
            } else {
              console.debug('[Socket] no ticket available after guest session mint; socket creation skipped.');
            }
          });
      });
  }

  private createSocketWithTicket(ticket: string) {
    console.debug('[Socket] creating socket with ticket (initial connect)');
    const socketOptions = deepCopy(SOCKET_OPTIONS);
    socketOptions.query = { ticket };

    const socket = this.socketService.init('/socket', socketOptions);
    // Refresh the single-use ticket on every reconnect attempt.
    socket.refreshTicketOnReconnect(
      () => this.authSessionService.getSocketTicket(),
      () => this.outageService.active,
    );
    // AIWB-1175: report socket drops to the outage service and reconnect once the platform is back.
    socket.watchOutage(
      { report: () => this.outageService.report('socket'), recovered$: this.outageService.recovered$ },
      () => this.authSessionService.getSocketTicket(),
    );
    // AIWB-1257: keep the socket alive after socket.io gives up or the server disconnects us; a 401 from the
    // ticket endpoint stops the loop and hands over to the AIWB-1088 session-expiry flow (inert for guests).
    socket.enableSelfHeal({
      credential: () => this.authSessionService.getSocketTicketResult(),
      skipWhen: () => this.outageService.active,
      onUnauthorized: () =>
        this.sessionExpiry.report('http-401', { method: 'GET', url: '/api/auth/socket-ticket', status: 401 }),
    });
    this.socketBankService.setSocket(SOCKET_BANK_ID_GLOBAL, socket);
  }

  private createSocketWithToken(accessToken: string) {
    if (!accessToken) {
      console.error('user unauthenticated. socket creation aborted.');
      return;
    }

    const socketOptions = deepCopy(SOCKET_OPTIONS);
    socketOptions.query = {
      token: encodeURIComponent(accessToken),
    };

    const socket = this.socketService.init('/socket', socketOptions);
    this.socketBankService.setSocket(SOCKET_BANK_ID_GLOBAL, socket);
  }

  private initGlobalNotifications() {
    this.socketBankService.globalSocket$
      .pipe(
        switchMap((socket) => {
          // AK-715: seed the notification centre's history now — globalSocket$ only emits
          // once a socket exists and is connected, which is exactly the authenticated-user
          // signal the rest of this method already depends on. load() no-ops without a
          // user, so this is safe even if that guarantee ever changes.
          this.notificationCenterService.load();
          this.liveDebug.socket = socket;

          return merge(
            socket.reconnected$.pipe(tap(({ downMs }) => this.onSocketReconnected(downMs))),
            socket.events$.pipe(
              filter((event) => this.isBridgedSocketEvent(event)),
              tap((event) => this.dispatchSocketEvent(event)),
            ),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    // AIWB-1257: when the platform comes back from an outage, refresh what is on screen once.
    this.outageService.recovered$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // The socket reconnects after an async ticket fetch; resyncing now would run before the rooms are
      // rejoined and the dedupe would swallow the reconnected$ resync. Let that path do it.
      if (this.liveDebug.socket && !this.liveDebug.socket.isConnected()) return;
      this.requestLiveResync('outage-recovered');
    });

    if (isDevMode()) {
      (window as unknown as { __aiwbLive?: () => unknown }).__aiwbLive = () => ({
        connected: this.liveDebug.socket?.isConnected() ?? false,
        lastEventAt: this.liveDebug.lastEventAt,
        lastEventId: this.liveDebug.lastEventId,
        lastReconnectAt: this.liveDebug.lastReconnectAt,
        lastResyncAt: this.liveDebug.lastResyncAt,
        bridgeErrors: this.liveDebug.bridgeErrors,
        dataTopics: this.dataTopicDiagnostics.snapshot(),
      });
      // WL-0463: drives a socket frame through the same allow-list, dispatch and diagnostics as a real one.
      (window as unknown as { __aiwbLiveEmit?: (m: SocketMessage) => boolean }).__aiwbLiveEmit = (message) => {
        const event = { eventName: SocketEventType.BROADCAST_DATA, message };
        if (!this.isBridgedSocketEvent(event)) return false;
        this.dispatchSocketEvent(event);
        return true;
      };
    }
  }

  private isBridgedSocketEvent(event: { eventName: SocketEventType; message: SocketMessage }): boolean {
    return (
      event.eventName === SocketEventType.BROADCAST_DATA &&
      !!event.message?.message_type &&
      this.allowedSocketMessageTypes.includes(event.message.message_type as MessageType)
    );
  }

  private lastResyncAt = 0;
  /** Dev-only diagnostics surfaced as window.__aiwbLive() (AIWB-1257 FR-3). */
  private readonly liveDebug: {
    socket?: SocketImpl;
    lastEventAt?: string;
    lastEventId?: string;
    lastReconnectAt?: string;
    lastResyncAt?: string;
    bridgeErrors: number;
  } = { bridgeErrors: 0 };

  /** AIWB-1257 catch-up: reload notifications on EVERY settled reconnect; refresh on-screen hosts after a real gap. */
  private onSocketReconnected(downMs: number): void {
    this.liveDebug.lastReconnectAt = new Date().toISOString();
    try {
      this.notificationCenterService.load();
      if (downMs >= LIVE_RESYNC_MIN_GAP_MS) this.requestLiveResync('socket-reconnected');
    } catch (err) {
      console.error('[Socket] reconnect catch-up failed', err);
    }
  }

  /** One refresh of every mounted broadcast host; a recovery and the reconnect that follows collapse into one. */
  private requestLiveResync(reason: string): void {
    const now = Date.now();
    if (now - this.lastResyncAt < LIVE_RESYNC_DEDUPE_MS) return;
    this.lastResyncAt = now;
    this.liveDebug.lastResyncAt = new Date(now).toISOString();
    console.debug('[Socket] live resync —', reason);
    this.broadcastService.broadcast(MessageType.LIVE_RESYNC, { reason });
  }

  /** AIWB-1257: one bad event must never tear down the bridge — log it and keep listening. */
  private dispatchSocketEvent(event: { eventName: SocketEventType; message: SocketMessage }): void {
    const envelope = event.message;
    try {
      this.liveDebug.lastEventAt = (envelope.timestamp as string | undefined) ?? new Date().toISOString();
      this.liveDebug.lastEventId = envelope.id;
      switch (envelope.message_type) {
        case MessageType.NOTIFICATION: {
          const message = envelope.data as SocketNotificationData | undefined;
          if (!message || typeof message !== 'object') {
            console.warn('[Socket] notification without data ignored', {
              id: envelope.id,
              timestamp: envelope.timestamp,
            });
            break;
          }
          this.globalNotificationService.show(message);
          // AK-715: the same event also feeds the persistent notification centre,
          // so a toast and its bell entry always agree.
          this.notificationCenterService.add(message, {
            broadcast_id: envelope.broadcast_id,
            created_ts: envelope.created_ts,
            timestamp: envelope.timestamp,
          });
          break;
        }

        case MessageType.USER_VAR: {
          const data = envelope.data as UserVariableOperationData | undefined;
          this.handleSetUserVars(data);
          break;
        }

        case MessageType.GLOBAL_BOT: {
          const message = envelope.data as GlobalBotMessageData;
          this.broadcastService.broadcast(MessageType.GLOBAL_BOT, message);
          break;
        }

        case MessageType.CONVERSATION_UPDATED: {
          const data = envelope.data as ConversationUpdatedData;
          this.broadcastService.broadcast(MessageType.CONVERSATION_UPDATED, data);
          break;
        }

        case MessageType.FORM_BROADCAST: {
          const message = envelope.data as FormOperationData | FormCommandBroadcastPayload;
          this.broadcastService.broadcast(MessageType.FORM_BROADCAST, message);
          break;
        }

        case MessageType.TABLE_BROADCAST: {
          const data = envelope.data as TableBroadcastPayload;
          this.broadcastService.broadcast(MessageType.TABLE_BROADCAST, data);
          break;
        }

        // AK-646: standalone collection sections are addressed by `collection_id`, not by
        // `form_id`, so they need their own envelope on the bus (see
        // CollectionSectionBroadcastHandler).
        case MessageType.COLLECTION_BROADCAST: {
          const data = envelope.data as CollectionCommandBroadcastPayload;
          this.broadcastService.broadcast(MessageType.COLLECTION_BROADCAST, data);
          break;
        }

        // WL-0463: the only case that forwards more than `envelope.data`. A malformed payload is still bridged
        // (recorded as invalid); DatasourceTopicHandler rejects it.
        case MessageType.DATASOURCE_BROADCAST: {
          const data = envelope.data as DatasourceBroadcastPayload | undefined;
          const ctx = {
            source: 'socket',
            room_type: envelope.room_type,
            meta: envelope.meta,
            envelope_id: envelope.id,
          } as const;
          const seq = this.dataTopicDiagnostics.recordEvent({ data, ...ctx });
          const busEvent: DatasourceBroadcastBusEvent = { data, ...ctx, seq };
          this.broadcastService.broadcast(MessageType.DATASOURCE_BROADCAST, busEvent);
          break;
        }
      }
    } catch (err) {
      this.liveDebug.bridgeErrors++;
      // broadcast_id is never on the wire — log the per-emit id and the server timestamp instead.
      console.error(
        '[Socket] broadcast handler failed',
        { message_type: envelope?.message_type, id: envelope?.id, timestamp: envelope?.timestamp },
        err,
      );
    }
  }

  private handleSetUserVars(data: UserVariableOperationData | undefined) {
    if (!data || typeof data !== 'object') {
      console.warn('user variable operation without data ignored');
      return;
    }
    const { key, value, values, keys, persist, action } = data;
    const operation = action ?? 'set';
    // One GlobalVarsService call per message, so a multi-key write emits one changes$ batch.
    if (operation === 'set') {
      const map = {
        ...(values && typeof values === 'object' && !Array.isArray(values) ? values : {}),
        ...(key ? { [key]: value } : {}),
      };
      if (!Object.keys(map).length) {
        console.warn('user variable set without key/values ignored');
        return;
      }
      this.globalVarsService.set(map, persist);
    } else if (operation === 'unset' || operation === 'remove' || operation === 'delete') {
      const list = [...new Set([...(Array.isArray(keys) ? keys : []), ...(key ? [key] : [])])];
      if (!list.length) {
        console.warn('user variable unset without key/keys ignored');
        return;
      }
      this.globalVarsService.unset(list, persist);
    }
  }

  // TODO: remove this method entirely. we don't want to refresh the follower tabs on entity switch
  //       as it is causing token race and wiping the OIDC runtime data.
  //       Instead, route to home and refresh the tabs on logout.
  private setupEntitySwitchListener(): void {
    /* this.tabSyncService.entitySwitch$.pipe(takeUntil(this.destroy$)).subscribe(({ entityId }) => {
      // When another tab switches entity, redirect this tab to root
      console.debug(`[Tab-Sync] Entity switched to ${entityId} in another tab. Redirecting to root...`);

      // Clear any module-specific state
      this.localstorageService.remove(STORAGE_KEY_ENTITY_ID);

      // Navigate to root - the app will reload with the new entity context
      this.router
        .navigate(['/'], {
          queryParams: {},
          queryParamsHandling: 'merge',
        })
        .then(() => {
          // Force a page reload to ensure clean state with new entity
          window.location.reload();
        });
    }); */
  }

  private setupFocusListener(): void {
    console.debug('Setting up focus listener');
    console.warn(`[AppComponent] Initial document.hasFocus(): ${document.hasFocus()}`);

    // Listen for window focus events
    fromEvent(window, 'focus')
      .pipe(
        switchMap(() =>
          forkJoin({
            user: this.userQuery.user$.pipe(take(1)),
            isAuthenticated: this.userService.isUserAuthenticated$.pipe(take(1)),
          }),
        ),
        tap(({ user, isAuthenticated }) => {
          const userId = user.user_id;
          const entityId = user.entity?.entity_id;

          // Check authentication state
          const storedIsLoggedIn = this.userService.getLoginStatus();

          if (this.userService.perceivedAuthenticationState !== storedIsLoggedIn) {
            const authStateChanged = isAuthenticated ? 'logged-in' : 'logged-out';
            this.systemNotification.showWithAction(
              `You were ${authStateChanged} in another tab. Press refresh to continue.`,
              'Refresh',
              () => {
                this.router.navigate(['/']).then(() => {
                  window.location.reload();
                });
              },
            );
            return;
          }

          if (!userId || !entityId) {
            return;
          }

          const concurrentEntityId = this.localstorageService.get(STORAGE_KEY_ENTITY_ID);
          const concurrentUserId = this.localstorageService.get(STORAGE_KEY_USER_ID);

          if (userId !== concurrentUserId || entityId !== concurrentEntityId) {
            const itemSwitched =
              userId !== concurrentUserId ? 'User' : entityId !== concurrentEntityId ? 'Entity' : null;

            if (itemSwitched) {
              this.systemNotification.showWithAction(
                `${itemSwitched} was switched in another tab. Press refresh to continue.`,
                'Refresh',
                () => {
                  this.router.navigate(['/']).then(() => {
                    window.location.reload();
                  });
                },
              );
            }
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        console.debug('Window gained focus');
        this.tabSyncService.onFocusChange(true);
      });

    // Listen for window blur events
    fromEvent(window, 'blur')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.debug('🔇 Window lost focus');
        this.tabSyncService.onFocusChange(false);
      });

    // Test that the listener is working with a delayed check
    setTimeout(() => {
      console.warn(`[AppComponent] Focus test - current hasFocus(): ${document.hasFocus()}`);
    }, 2000);

    // If this tab has focus on initialization, try to become leader
    if (document.hasFocus()) {
      console.debug('Tab has focus on init, notifying TabSync service');
      this.tabSyncService.onFocusChange(true);
    }
  }

  private setupTokenRenewalMonitoring(): void {
    // Monitor leadership changes and manage token renewal accordingly
    this.tabSyncService
      .getIsLeader$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLeader) => {
        if (isLeader) {
          console.debug('Became leader, checking if auth is complete for token renewal');
          if (this.isAuthCompleted) {
            this.setupTokenRenewalTimer();
          } else {
            console.debug('⏳ Auth not complete yet, token renewal will start after auth');
          }
        } else {
          console.debug('Lost leadership, clearing token renewal timer');
          this.clearTokenRenewalTimer();
        }
      });
  }

  private getOidcRuntimeData() {
    return this.oidcSecurityService.getConfiguration().pipe(
      map((configuration) => configuration?.clientId),
      map((clientId) => `0-${clientId}`),
    );
  }

  private getValueFromOidcData(key: string) {
    return this.getOidcRuntimeData().pipe(
      map((oidcStorageKey) => {
        if (!oidcStorageKey) {
          console.debug('No OIDC storage key found');
          return null;
        }

        try {
          const oidcData = JSON.parse(localStorage.getItem(oidcStorageKey) || '{}');
          const value = getNestedValue(oidcData, key);

          if (!value) {
            console.debug('requested value not found');
            return null;
          }

          return value as unknown;
        } catch (e) {
          console.warn('Error reading OIDC data', e);
          return null;
        }
      }),
    );
  }

  getOidcTokenExpiry() {
    return this.getValueFromOidcData('access_token_expires_at').pipe(
      map((expiryTime) => {
        if (!expiryTime) {
          console.debug('No access_token_expires_at found in OIDC data');
          return null;
        }

        const expiryDate = new Date(expiryTime as number);
        console.warn(`[AppComponent] Token expiry time from OIDC data: ${expiryDate.toLocaleString()} (${expiryTime})`);
        return expiryTime as number;
      }),
    );
  }

  getOidcExpiresIn() {
    return this.getValueFromOidcData('authnResult.expires_in').pipe(map((expiresInTime) => expiresInTime as number));
  }

  private setupTokenRenewalTimer(): void {
    console.debug('Setting up token renewal timer as leader');

    // Clear any existing timer
    this.clearTokenRenewalTimer();

    const renewTimeBeforeTokenExpires$ = this.oidcSecurityService
      .getConfiguration()
      .pipe(map((config) => config?.renewTimeBeforeTokenExpiresInSeconds || 60));

    forkJoin([this.getOidcTokenExpiry(), this.getOidcExpiresIn(), renewTimeBeforeTokenExpires$])
      .pipe(
        tap(([tokenExpiry, expiresIn, renewTimeBeforeTokenExpiresInSeconds]) => {
          if (!tokenExpiry) {
            console.debug('No token expiry found, skipping timer setup');
            return;
          }

          const renewBeforeExpiryMs = renewTimeBeforeTokenExpiresInSeconds * 1000;
          const expiresInMs = expiresIn * 1000;

          const now = Date.now();
          const renewalTime = tokenExpiry - Math.min(renewBeforeExpiryMs, expiresInMs - 120 * 1000);
          const timeUntilRenewal = renewalTime - now;

          const renewalDate = new Date(renewalTime);
          console.debug(`Renewal scheduled at: ${renewalDate.toLocaleString()} (${renewalTime})`);
          console.debug(
            `Timer set for: ${Math.round(timeUntilRenewal / 1000)}s ` +
              `(${Math.round(timeUntilRenewal / 60000)} minutes)`,
          );

          if (timeUntilRenewal <= 0) {
            console.debug('Token needs immediate renewal');
            this.renewToken();
            return;
          }

          this.tokenRenewalTimer = setTimeout(() => {
            this.renewToken();
          }, timeUntilRenewal);
        }),
      )
      .subscribe();
  }

  private clearTokenRenewalTimer(): void {
    if (this.tokenRenewalTimer) {
      console.debug('Clearing token renewal timer');
      clearTimeout(this.tokenRenewalTimer);
      this.tokenRenewalTimer = null;
    }
  }

  private reconnectGlobalSocket(accessToken: string): void {
    console.debug('Reconnecting global socket with refreshed token');

    // Close existing socket
    const existingSocket = this.socketBankService.getSocket(SOCKET_BANK_ID_GLOBAL);
    if (existingSocket) {
      existingSocket.close();
    }

    // Initialize new socket with the provided fresh token
    this.initSocket(accessToken);
  }

  private renewToken(): void {
    const renewalStartTime = new Date();
    console.debug(`ATTEMPTING TOKEN RENEWAL at ${renewalStartTime.toLocaleString()}`);

    this.oidcSecurityService.forceRefreshSession().subscribe({
      next: (loginResponse) => {
        const responseTime = new Date();
        console.debug(`Token renewal API response received at ${responseTime.toLocaleString()}`);

        if (loginResponse.isAuthenticated && loginResponse.accessToken) {
          console.debug('TOKEN RENEWAL SUCCESSFUL - Reconnecting socket and setting up next timer');
          // Reconnect socket with the new token from the response
          this.reconnectGlobalSocket(loginResponse.accessToken);
          // Set up next renewal timer
          this.setupTokenRenewalTimer();
        } else {
          console.warn('TOKEN RENEWAL FAILED - User not authenticated');
          console.warn('Error details:', loginResponse.errorMessage);
        }
      },
      error: (error) => {
        const errorTime = new Date();
        console.warn(`TOKEN RENEWAL ERROR at ${errorTime.toLocaleString()}:`, error);
        console.debug('Retrying in 5 minutes...');
        // Retry in 5 minutes
        this.tokenRenewalTimer = setTimeout(
          () => {
            this.renewToken();
          },
          5 * 60 * 1000,
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.clearTokenRenewalTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
